import { type FormEvent, type ReactElement, useCallback, useEffect, useRef, useState } from 'react';
import { type ApiError, type GovernedSettingChangeView, type SessionRevocationTtlView } from '@vello/api-client';
import { type FormatContext } from '@vello/format';
import { maySelfApprove, CHANGE_STATUS_TONE, canChangeTtl, finishTtlLoad, initialTtlScreenState, isTtlViewFresh, needsTtlRefresh, startTtlLoad, ttlDecisionBasisChanged, type TtlScreenState, type TtlChangeField, type TtlChangeForm, approveSessionRevocationTtl, emptyTtlChangeForm, formatMs, loadSessionRevocationTtl, normalizeTtlChangeForm, pendingActionsFor, proposeSessionRevocationTtl, rejectSessionRevocationTtl, ttlWriteNotice, type TtlWriteNotice, formatMarketInstant, marketZoneOfContext, scopeTimeZone, tailOf } from '../../src/index';
import { SelfApprovalNote } from '../components/self-approval-note';
import { useAdmin } from '../session';
import { useConsole } from '../console';
import { useT } from '../i18n/provider';
import { useVocab } from '../i18n/vocab';
import { Badge, Button, EmptyState, Field, Notice, PageHeader, Panel, StatCard, Table, TextInput, type Column, type ToastSpec, SectionTitle, FormGrid, Cluster, Grid, Stack, Text } from '@vello/web-theme';
import { PersonName, usePersonName } from '../components/person-name';
import { LoadingBlock } from '../components/loading-block';
import { DetailRow as Row } from '../components/detail-row';

/** Replicas re-read the governed value at this cadence when the API does not report it. */
const DEFAULT_REFRESH_S = 10;
/** How often the screen re-evaluates freshness (cheap; no request unless a reload is due). */
const FRESHNESS_TICK_MS = 1_000;

/**
 * The toast for a committed governance write (Codex r4 P2-2). An applied change that the answering API replica could not
 * re-read is committed but not yet enforced there: it warns, with an alert icon, instead of the plain success.
 */
function writeToast(notice: TtlWriteNotice, message: string): ToastSpec {
  return notice.tone === 'warning' ? { message, tone: 'warning', icon: 'shieldAlert' } : { message, tone: notice.tone };
}

/**
 * **Segurança** (D342) — the platform security controls a super admin tunes live. The first control is the AR-2a
 * **session-revocation window**: how long the session guard may cache an ACTIVE verdict, i.e. how long a revoked token
 * may still pass.
 *
 * The screen shows the current value, its version, who changed it and when (and who approved an increase), this API
 * replica's live value, the pending four-eyes approval, and the audit trail. A decrease applies at once; an increase
 * waits for a second super admin. Every change is confirmed in the console dialog, never a native one. The server is
 * authoritative on bounds, four-eyes, and the GLOBAL grant.
 *
 * Freshness (Codex r3 P2-5): a read is treated as effective for 30 s only.
 * - The screen reloads on window focus and when the deadline passes; an expired or failed read is labelled outdated and
 *   blocks changes.
 * - Every change reloads right before its confirmation dialog, and again right before submission. If the value or the
 *   pending request changed meanwhile, the change stops and the new facts are shown.
 */
export function SecurityScreen(): ReactElement {
  const { runtime, me, scope: operatorScope, markets, grants } = useAdmin();
  const selfApproval = maySelfApprove(grants); // D868: a GLOBAL super administrator decides their own proposals
  const { confirm, toast } = useConsole();
  const { t, lang } = useT();
  const { v, fail, explain } = useVocab();
  // D401: instants render in the operating market's zone (AGENTS: store UTC, render in the market zone).
  // When the scope names no single zone the render falls back to UTC and SAYS so (`formatMarketInstant`).
  const fmtCtx: FormatContext = { locale: lang, timeZone: scopeTimeZone(operatorScope.current, markets) ?? 'UTC', measurement: 'metric' };
  const ms = (value: number): string => formatMs(value, lang);

  // Codex r1 P2-6: one generation-guarded screen state. A failed reload marks the last read stale (never "effective")
  // and blocks changes until a fresh read succeeds; an older response can never overwrite a newer one.
  const [screen, setScreen] = useState<TtlScreenState>(() => initialTtlScreenState());
  const [nowMs, setNowMs] = useState<number>(() => Date.now());
  const generation = useRef(0);
  const view = screen.view;
  const requesterName = usePersonName(view?.pending?.requestedBy);
  const [form, setForm] = useState<TtlChangeForm>(() => emptyTtlChangeForm());
  const [errors, setErrors] = useState<Partial<Record<TtlChangeField, string>>>({});
  const [busy, setBusy] = useState(false);

  const errorText = useCallback(
    (e: ApiError): string => {
      return explain(e) ?? t('sec.err.generic', { code: fail(e) });
    },
    [t, fail, explain],
  );

  /** Read the current state; resolves to the fresh view (or undefined when the read failed or was overtaken). */
  const reload = useCallback(async (): Promise<SessionRevocationTtlView | undefined> => {
    const g = ++generation.current;
    setScreen((s) => startTtlLoad(s, g, Date.now()));
    const res = await loadSessionRevocationTtl(runtime);
    const finishedAt = Date.now();
    setNowMs(finishedAt);
    setScreen((s) => finishTtlLoad(s, g, res, finishedAt));
    return res.ok && g === generation.current ? res.value : undefined;
  }, [runtime]);

  // Initial read, reload on window focus, and a freshness tick.
  useEffect(() => {
    void reload();
    const onFocus = (): void => {
      void reload();
    };
    window.addEventListener('focus', onFocus);
    const tick = window.setInterval(() => setNowMs(Date.now()), FRESHNESS_TICK_MS);
    return () => {
      window.removeEventListener('focus', onFocus);
      window.clearInterval(tick);
    };
  }, [reload]);

  // Reload when the freshness deadline passes (never while a read is in flight; a failure retries after a full period).
  useEffect(() => {
    if (view !== undefined && needsTtlRefresh(screen, nowMs)) void reload();
  }, [nowMs, screen, view, reload]);

  const loadError = screen.error !== undefined ? errorText(screen.error) : undefined;
  const fresh = isTtlViewFresh(screen, nowMs);
  const canChange = canChangeTtl(screen, nowMs);

  const refreshS = view?.runtime !== undefined ? Math.max(1, Math.round(view.runtime.refreshIntervalMs / 1000)) : DEFAULT_REFRESH_S;

  const onSubmit = async (e: FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    if (busy || !canChange) return;
    // Reload right before confirmation, so the decision is made against the current value.
    setBusy(true);
    const before = await reload();
    setBusy(false);
    if (before === undefined) {
      toast({ message: t('sec.toast.reloadFailed'), tone: 'warning' });
      return;
    }
    const normalized = normalizeTtlChangeForm(form, before);
    if (!normalized.ok) {
      const next: Partial<Record<TtlChangeField, string>> = {};
      for (const issue of normalized.error) next[issue.field] = t(`sec.err.${issue.code}`);
      setErrors(next);
      return;
    }
    setErrors({});
    const { valueMs, reason, kind, fromMs } = normalized.value;
    const outcome = kind === 'TIGHTEN' ? t('sec.form.preview.tighten', { s: refreshS }) : t('sec.form.preview.loosen');
    const answer = await confirm({
      title: t(kind === 'TIGHTEN' ? 'sec.form.confirm.tighten' : 'sec.form.confirm.loosen'),
      body: t('sec.form.confirm.body', { from: ms(fromMs), to: ms(valueMs), outcome }),
      confirmLabel: t('sec.form.confirm.ok'),
      cancelLabel: t('ui.cancel'),
      tone: kind === 'LOOSEN' ? 'danger' : 'primary',
      icon: kind === 'LOOSEN' ? 'shieldAlert' : 'lock',
    });
    if (!answer.confirmed) return;
    setBusy(true);
    // …and again right before submission: if the basis changed while the dialog was open, stop and show the new facts.
    const latest = await reload();
    if (latest === undefined || ttlDecisionBasisChanged(before, latest)) {
      setBusy(false);
      toast({ message: t('sec.toast.changedMeanwhile'), tone: 'warning' });
      return;
    }
    const res = await proposeSessionRevocationTtl(runtime, { valueMs, reason });
    setBusy(false);
    if (res.ok) {
      // Codex r4 P2-2: an applied change this replica could not re-read is committed but not yet enforced here.
      const notice = ttlWriteNotice(res.value);
      toast(writeToast(notice, t(notice.key, { to: ms(valueMs) })));
      setForm(emptyTtlChangeForm());
    } else {
      toast({ message: errorText(res.error), tone: 'warning' });
    }
    await reload();
  };

  const resolvePending = async (action: 'approve' | 'reject', shown: GovernedSettingChangeView, own: boolean): Promise<void> => {
    if (busy || !canChange) return;
    // Reload right before confirmation: the request must still be the pending one the operator is looking at.
    setBusy(true);
    const before = await reload();
    setBusy(false);
    const pending = before?.pending;
    if (before === undefined || pending === null || pending === undefined || pending.id !== shown.id) {
      toast({ message: t('sec.toast.changedMeanwhile'), tone: 'warning' });
      return;
    }
    const from = ms(pending.fromValue);
    const to = ms(pending.toValue);
    const answer = await confirm({
      ...(action === 'approve'
        ? { title: t('sec.approve.confirm.title'), body: t('sec.approve.confirm.body', { from, to }), confirmLabel: t('sec.pending.approve'), tone: 'danger' as const, icon: 'shieldAlert' as const }
        : { title: t(own ? 'sec.withdraw.confirm.title' : 'sec.reject.confirm.title'), body: t('sec.reject.confirm.body', { from }), confirmLabel: t(own ? 'sec.pending.withdraw' : 'sec.pending.reject'), tone: 'primary' as const, icon: 'lock' as const }),
      cancelLabel: t('ui.cancel'),
      reasons: [t('sec.reason.reviewed'), t('sec.reason.incident'), t('sec.reason.capacity'), t('sec.reason.other')],
      reasonLabel: t('sec.reason.label'),
      notePlaceholder: t('sec.reason.note'),
    });
    if (!answer.confirmed || answer.reason === undefined) return;
    const reason = answer.note !== undefined ? `${answer.reason} — ${answer.note}` : answer.reason;
    setBusy(true);
    // …and again right before submission.
    const latest = await reload();
    if (latest === undefined || ttlDecisionBasisChanged(before, latest)) {
      setBusy(false);
      toast({ message: t('sec.toast.changedMeanwhile'), tone: 'warning' });
      return;
    }
    const res = action === 'approve' ? await approveSessionRevocationTtl(runtime, pending.id, reason) : await rejectSessionRevocationTtl(runtime, pending.id, reason);
    setBusy(false);
    if (res.ok) {
      // Codex r4 P2-2: an approved change this replica could not re-read is committed but not yet enforced here. The
      // server's outcome, not a local guess, says whether a reject was recorded as a withdrawal.
      const notice = ttlWriteNotice(res.value);
      toast(writeToast(notice, t(notice.key, { to })));
    } else {
      toast({ message: errorText(res.error), tone: 'warning' });
    }
    await reload();
  };

  const columns: readonly Column<GovernedSettingChangeView>[] = [
    { key: 'when', header: t('sec.col.when'), render: (c) => <span className="tnum">{formatMarketInstant(c.resolvedAt ?? c.requestedAt, marketZoneOfContext(fmtCtx), fmtCtx.locale)}</span> },
    { key: 'change', header: t('sec.col.change'), render: (c) => <span className="tnum">{`${ms(c.fromValue)} → ${ms(c.toValue)} ms`}</span> },
    { key: 'status', header: t('sec.col.status'), render: (c) => <Badge tone={CHANGE_STATUS_TONE[c.status] ?? 'neutral'}>{v('settings.change_status', c.status)}</Badge> },
    { key: 'requestedBy', header: t('sec.col.requestedBy'), render: (c) => <PersonName id={c.requestedBy} /> },
    { key: 'resolvedBy', header: t('sec.col.resolvedBy'), render: (c) => <><PersonName id={c.resolvedBy} /><SelfApprovalNote record={c} /></> },
    { key: 'reason', header: t('sec.col.reason'), render: (c) => c.resolutionReason ?? c.requestReason },
    { key: 'code', header: t('sec.col.code'), render: (c) => <span>{v('platform.decision_reason', c.reasonCode)}</span> },
  ];

  if (view === undefined) {
    return (
      <>
        <PageHeader title={t('sec.title')} subtitle={t('sec.subtitle')} />
        {loadError !== undefined ? (
          <Stack gap={3} align="start">
            <Notice tone="danger">{loadError}</Notice>
            <Button variant="secondary" icon="rotateCcw" onClick={() => void reload()}>
              {t('sec.stale.retry')}
            </Button>
          </Stack>
        ) : (
          <Panel>
            <LoadingBlock />
          </Panel>
        )}
      </>
    );
  }

  const effective = view.effective;
  const bounds = t('sec.ttl.bounds', { min: ms(view.bounds.minMs), max: ms(view.bounds.maxMs), def: ms(view.bounds.defaultMs) });
  const pending = view.pending;
  const actions = pendingActionsFor(pending, me?.id, selfApproval);
  const draft = form.valueMs.trim() !== '' ? normalizeTtlChangeForm({ valueMs: form.valueMs, reason: '-' }, view) : undefined;
  const draftKind = draft !== undefined && draft.ok ? draft.value.kind : undefined;
  const expired = !screen.stale && !fresh;

  return (
    <>
      <PageHeader title={t('sec.title')} subtitle={t('sec.subtitle')} />
      {screen.stale && (
        <Stack gap={3} align="start">
          <Notice tone="warning">{t('sec.stale', { detail: loadError ?? '' })}</Notice>
          <Button variant="secondary" icon="rotateCcw" onClick={() => void reload()}>
            {t('sec.stale.retry')}
          </Button>
        </Stack>
      )}
      {expired && (
        <Notice tone="neutral">{t('sec.fresh.expired')}</Notice>
      )}

      <Stack gap={5}>
        <Panel>
          <Stack gap={4}>
            <SectionTitle>{t('sec.ttl.title')}</SectionTitle>
            <Text as="p" tone="secondary">{t('sec.ttl.explain')}</Text>
            {!effective.valid && (
              <Notice tone="danger">{t('sec.ttl.invalid', { n: effective.version ?? '—', enforced: ms(effective.enforcedValueMs) })}</Notice>
            )}
            <Grid min="sm" gap={3}>
              <StatCard label={t(fresh ? 'sec.ttl.effective' : 'sec.ttl.effective.stale')} value={effective.valid ? t('sec.ttl.ms', { value: ms(effective.valueMs) }) : '—'} hint={bounds} />
              <StatCard
                label={t('sec.ttl.version')}
                value={effective.version !== undefined ? t('sec.ttl.version.value', { n: effective.version }) : t('sec.ttl.version.default')}
                {...(effective.version === undefined ? { hint: t('sec.ttl.version.defaultHint') } : {})}
              />
              {view.runtime !== undefined && (
                <StatCard subtle label={t('sec.ttl.runtime')} value={t('sec.ttl.ms', { value: ms(view.runtime.valueMs) })} hint={v('settings.ttl_runtime_status', view.runtime.status, { s: refreshS })} />
              )}
            </Grid>
            {effective.valid && effective.changedBy !== undefined && (
              <div>
                <Row label={t('sec.ttl.changedBy')} mono={false}><PersonName id={effective.changedBy} /></Row>
                {effective.changedAt !== undefined && <Row label={t('sec.ttl.changedAt')} value={formatMarketInstant(effective.changedAt, marketZoneOfContext(fmtCtx), fmtCtx.locale)} />}
                {effective.approvedBy !== undefined && <Row label={t('sec.ttl.approvedBy')} mono={false}><PersonName id={effective.approvedBy} /></Row>}
                {effective.effectiveFrom !== undefined && <Row label={t('sec.ttl.effectiveFrom')} value={formatMarketInstant(effective.effectiveFrom, marketZoneOfContext(fmtCtx), fmtCtx.locale)} />}
              </div>
            )}
          </Stack>
        </Panel>

        {pending !== null && (
          <Panel>
            <Stack gap={4}>
              <SectionTitle>{t('sec.pending.title')}</SectionTitle>
              <Text as="p" tone="secondary">{t('sec.pending.fourEyes')}</Text>
              <Notice tone="warning">
                {t('sec.pending.body', { who: requesterName ?? tailOf(pending.requestedBy), from: ms(pending.fromValue), to: ms(pending.toValue), at: formatMarketInstant(pending.requestedAt, marketZoneOfContext(fmtCtx), fmtCtx.locale), reason: pending.requestReason })}
              </Notice>
              {actions.isOwnRequest && !actions.canApprove && <Text as="p" tone="secondary">{t('sec.pending.own')}</Text>}
              <Cluster gap={3} wrap>
                {actions.canApprove && (
                  <Button variant="secondary" icon="shieldAlert" disabled={busy || !canChange} onClick={() => void resolvePending('approve', pending, actions.isOwnRequest)}>
                    {t('sec.pending.approve')}
                  </Button>
                )}
                {actions.canReject && (
                  <Button variant="secondary" disabled={busy || !canChange} onClick={() => void resolvePending('reject', pending, actions.isOwnRequest)}>
                    {t(actions.isOwnRequest ? 'sec.pending.withdraw' : 'sec.pending.reject')}
                  </Button>
                )}
              </Cluster>
            </Stack>
          </Panel>
        )}

        <Panel>
          <Stack gap={4}>
            <SectionTitle>{t('sec.form.title')}</SectionTitle>
            <form onSubmit={(e) => void onSubmit(e)} noValidate>
              <Stack gap={4}>
                <FormGrid>
                  <Field label={t('sec.form.value')} htmlFor="sec-ttl-value" hint={errors.valueMs ?? bounds}>
                    <TextInput
                      id="sec-ttl-value"
                      className="tnum"
                      inputMode="numeric"
                      autoComplete="off"
                      value={form.valueMs}
                      aria-invalid={errors.valueMs !== undefined}
                      onChange={(e) => setForm((f) => ({ ...f, valueMs: e.target.value.replace(/\D/g, '') }))}
                      placeholder={String(view.bounds.defaultMs)}
                    />
                  </Field>
                  <Field label={t('sec.form.reason')} htmlFor="sec-ttl-reason" hint={errors.reason ?? t('sec.form.reason.hint')}>
                    <TextInput id="sec-ttl-reason" value={form.reason} maxLength={500} aria-invalid={errors.reason !== undefined} onChange={(e) => setForm((f) => ({ ...f, reason: e.target.value }))} />
                  </Field>
                </FormGrid>
                {draftKind !== undefined && (
                  <Notice tone={draftKind === 'TIGHTEN' ? 'success' : 'warning'}>
                    {draftKind === 'TIGHTEN' ? t('sec.form.preview.tighten', { s: refreshS }) : t('sec.form.preview.loosen')}
                  </Notice>
                )}
                <Button type="submit" variant="primary" disabled={busy || !canChange}>
                  {busy ? t('sec.form.busy') : t('sec.form.submit')}
                </Button>
              </Stack>
            </form>
          </Stack>
        </Panel>

        <Panel>
          <Stack gap={4}>
            <Text as="h2" role="title">{t('sec.history.title')}</Text>
            <Table columns={columns} rows={view.history} rowKey={(c) => c.id} empty={<EmptyState title={t('sec.history.empty')} />} />
          </Stack>
        </Panel>
      </Stack>
    </>
  );
}
